import { useLanguage } from "@/i18n/LanguageContext";

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const avatarColors = ["#047857", "#064e3b", "#ea580c", "#059669", "#0d9488"];

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

const serviceDisplayMap: Record<string, string> = {
  "Sliding Door Repair": "reviews.filterSliding",
  "Glass Replacement": "reviews.filterGlass",
  "Window Hardware Repair": "reviews.filterHardware",
  "Weatherstripping & Sealing": "reviews.filterWeather",
  "Window Cleaning & Maintenance": "reviews.filterMaintenance",
  "Emergency Board-Up": "reviews.filterEmergency",
};

interface StoryCardProps {
  story: {
    id: number;
    name: string;
    neighborhood: string;
    service: string;
    date: string;
    hasPhotos: boolean;
    title: string;
    bodyEn: string;
    bodyEs: string;
  };
}

export default function StoryCard({ story }: StoryCardProps) {
  const { t, currentLang } = useLanguage();
  const color = getAvatarColor(story.name);
  const initials = getInitials(story.name);

  const locale = currentLang === "es" ? "es-US" : "en-US";
  const formattedDate = new Date(story.date).toLocaleDateString(locale, {
    month: "short",
    year: "numeric",
  });

  const body = currentLang === "es" ? story.bodyEs : story.bodyEn;
  const serviceKey = serviceDisplayMap[story.service] || story.service;

  return (
    <div className="bg-white rounded-xl border-l-4 border-primary p-5 md:p-6 shadow-sm mb-6">
      {/* Row 1: Avatar + Name + Project Example pill + Date */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
            style={{ backgroundColor: color }}
          >
            {initials}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-bold text-secondary">{story.name}</span>
              <span className="flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                <i className="ri-file-list-3-line text-xs" />
                {t("reviews.projectExample")}
              </span>
            </div>
          </div>
        </div>
        <span className="text-xs text-stone-400 shrink-0">{formattedDate}</span>
      </div>

      {/* Row 2: Neighborhood + Service pills */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs bg-[#fefce8] text-secondary px-2.5 py-1 rounded-full font-medium">
          {story.neighborhood}
        </span>
        <span className="text-xs bg-[#fefce8] text-secondary px-2.5 py-1 rounded-full font-medium">
          {t(serviceKey)}
        </span>
      </div>

      {/* Row 3: Title */}
      <h3 className="text-lg font-bold text-secondary mb-2 leading-snug">{story.title}</h3>

      {/* Row 4: Body */}
      <p className="text-[15px] leading-relaxed text-[#374151] mb-4">{body}</p>

      {/* Row 5: Photos */}
      {story.hasPhotos && (
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative w-full sm:w-[240px] h-[180px] rounded-lg overflow-hidden">
            <img
              src="https://placehold.co/240x180/064e3b/ffffff?text=Before"
              alt={`Window repair example before ${story.neighborhood}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "https://placehold.co/240x180/064e3b/ffffff?text=Before";
              }}
            />
            <span className="absolute top-2 left-2 bg-white text-secondary text-xs font-bold px-2 py-1 rounded-full">
              {t("reviews.before")}
            </span>
          </div>
          <div className="relative w-full sm:w-[240px] h-[180px] rounded-lg overflow-hidden">
            <img
              src="https://placehold.co/240x180/047857/ffffff?text=After"
              alt={`Window repair example after ${story.neighborhood}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "https://placehold.co/240x180/047857/ffffff?text=After";
              }}
            />
            <span className="absolute top-2 left-2 bg-white text-secondary text-xs font-bold px-2 py-1 rounded-full">
              {t("reviews.after")}
            </span>
          </div>
        </div>
      )}

      {/* Row 6: Privacy disclaimer */}
      <div className="pt-3 border-t border-stone-100">
        <p className="text-xs italic text-stone-400">
          {t("reviews.privacy")}
        </p>
      </div>
    </div>
  );
}